/**
 * My Book Client List
 * Simplified client list showing client name and portfolio count
 * Used in My Book view (cross-client portfolio analysis)
 */

import React, { useState } from 'react';
import { Eye, ChevronUp, ChevronDown, CalendarClock, PieChart } from 'lucide-react';
import { MyBookClient } from '../../hooks/useMyBookData';

// TASK-603: honest fallback reason per status when no alert message is available.
const STATUS_REASON: Partial<Record<MyBookClient['status'], string>> = {
  no_risk_profile: 'No risk profile on file',
  attention: 'Needs attention',
  rebalance: 'Portfolio drifted from its mandate',
};

interface MyBookClientListProps {
  clients: MyBookClient[];
  onClientClick: (clientId: string) => void;
  /** TASK-603: clientId → top human-readable reason ("why does this client need me"). */
  reasons?: Record<string, string>;
  onPrepMeeting?: (clientId: string) => void;
  onShowPortfolio?: (clientId: string) => void;
}

const MyBookClientList: React.FC<MyBookClientListProps> = ({
  clients,
  onClientClick,
  reasons,
  onPrepMeeting,
  onShowPortfolio,
}) => {
  const [sortField, setSortField] = useState<'customerName' | 'portfolioCount' | 'totalAuM'>('totalAuM');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Handle sort
  const handleSort = (field: 'customerName' | 'portfolioCount' | 'totalAuM') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Sort clients
  const sortedClients = [...clients].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }

    const aStr = String(aValue);
    const bStr = String(bValue);
    return sortDirection === 'asc'
      ? aStr.localeCompare(bStr)
      : bStr.localeCompare(aStr);
  });

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    // TASK-877: the client name column is sticky.
    //
    // On the canvas this table lives in a card measured at `clientWidth 545` against
    // `scrollWidth 855` at 1280x800, so `View / Prep / Portfolio` sit outside the visible
    // region behind a horizontal scrollbar. With no sticky column, scrolling to the actions
    // scrolled the name away — the advisor was choosing an action with no way to tell whose
    // row it belonged to. Same class as TASK-844.
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th
              className="sticky left-0 z-20 bg-gray-50 border-r border-gray-200 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => handleSort('customerName')}
            >
              <div className="flex items-center">
                Client Name
                {sortField === 'customerName' && (
                  sortDirection === 'asc' ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />
                )}
              </div>
            </th>
            <th
              className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => handleSort('portfolioCount')}
            >
              <div className="flex items-center justify-center">
                Portfolios
                {sortField === 'portfolioCount' && (
                  sortDirection === 'asc' ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />
                )}
              </div>
            </th>
            <th
              className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => handleSort('totalAuM')}
            >
              <div className="flex items-center justify-end">
                Total AuM
                {sortField === 'totalAuM' && (
                  sortDirection === 'asc' ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />
                )}
              </div>
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Risk Profile
            </th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {sortedClients.map((client) => (
            <tr key={client.customerId} className="group hover:bg-gray-50">
              {/* The sticky cell needs its own opaque background to cover the columns
                  sliding under it, which would otherwise hide the row's hover state —
                  hence `group` above and `group-hover` here. */}
              <td className="sticky left-0 z-10 bg-white group-hover:bg-gray-50 border-r border-gray-200 px-6 py-4">
                <div className="whitespace-nowrap text-sm font-medium text-gray-900">
                  {client.customerName}
                </div>
                {/* TASK-603: the reason, not the UUID — why this client needs attention.
                    Strip a leading client-name echo (backend messages repeat it). */}
                {(() => {
                  let reason = reasons?.[client.customerId] ?? STATUS_REASON[client.status];
                  if (reason && reason.toLowerCase().startsWith(client.customerName.toLowerCase())) {
                    reason = reason.slice(client.customerName.length).replace(/^[\s:,-]+/, '');
                    reason = reason.charAt(0).toUpperCase() + reason.slice(1);
                  }
                  return reason ? (
                    <div className="mt-0.5 flex items-start gap-1 text-xs text-amber-700">
                      <span aria-hidden>⚠</span>
                      <span className="min-w-0">{reason}</span>
                    </div>
                  ) : null;
                })()}
                {/* TASK-875: the figure the rebalance filter actually used, labelled.
                    The row's status comes from `breached_slots`; the number that used to be
                    available was `drift_percentage`, an aggregate the ±band is not measured
                    against. Marius Huber scores 2.24 there with a largest slot deviation of
                    1.67 and no breach — correctly absent from the rebalance set, on a figure
                    that reads as over a 2.0 tolerance. Showing the deviation and the band
                    together is what makes that absence explainable from the row. */}
                {client.driftHeadline && (
                  <div
                    className="mt-0.5 text-xs text-gray-500"
                    title={client.driftHeadline.label}
                    data-testid="drift-headline"
                  >
                    {client.driftHeadline.breaches.length > 0
                      ? `${client.driftHeadline.breaches[0].slot} `
                        + `${(client.driftHeadline.breaches[0].drift_pp ?? 0) > 0 ? '+' : ''}`
                        + `${(client.driftHeadline.breaches[0].drift_pp ?? 0).toFixed(2)}pp`
                      : `largest deviation ${client.driftHeadline.value.toFixed(2)}pp`}
                    <span className="text-gray-400">
                      {' '}· ±{client.driftHeadline.tolerance_pct}pp band
                    </span>
                  </div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {client.portfolioCount} {client.portfolioCount === 1 ? 'portfolio' : 'portfolios'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                {formatCurrency(client.totalAuM)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  client.riskProfile === 'CONSERVATIVE' ? 'bg-green-100 text-green-800' :
                  client.riskProfile === 'MODERATE' ? 'bg-yellow-100 text-yellow-800' :
                  client.riskProfile === 'AGGRESSIVE' ? 'bg-red-100 text-red-800' :
                  'bg-blue-100 text-blue-800'
                }`}>
                  {client.riskProfile}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center">
                <div className="inline-flex items-center gap-3">
                  <button
                    onClick={() => onClientClick(client.customerId)}
                    className="text-blue-600 hover:text-blue-900 inline-flex items-center gap-1 text-sm"
                    title="View Client"
                  >
                    <Eye className="w-4 h-4" />
                    View
                  </button>
                  {onPrepMeeting && (
                    <button
                      onClick={() => onPrepMeeting(client.customerId)}
                      className="text-gray-600 hover:text-gray-900 inline-flex items-center gap-1 text-sm"
                      title="Prep meeting"
                    >
                      <CalendarClock className="w-4 h-4" />
                      Prep
                    </button>
                  )}
                  {onShowPortfolio && (
                    <button
                      onClick={() => onShowPortfolio(client.customerId)}
                      className="text-gray-600 hover:text-gray-900 inline-flex items-center gap-1 text-sm"
                      title="Show portfolio"
                    >
                      <PieChart className="w-4 h-4" />
                      Portfolio
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default MyBookClientList;
